import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FormRendererProvider } from './FormRendererContext';
import { FieldSwitch } from './fields/FieldSwitch';
import { useFormRenderer } from './useFormRenderer';
import type { FormEnvelope } from './types';

/**
 * Turns any stored M1-17 schema into a live, submittable form (M1-18). The
 * web-specific half of the renderer — `useFormRenderer.ts` and the rest of
 * this module carry all the DOM/React-DOM-specific pieces (field widgets,
 * Tailwind classes, `<canvas>`/`<input type="file">`); the schema
 * interpretation, value state, and validation live in `useFormRenderer.ts`
 * and have no such dependency, so a React Native port (M3-12) reuses that
 * hook and supplies its own widgets here instead of starting over.
 *
 * This component has no idea what it's attached to (a job, a customer, a
 * standalone form) or how a submission gets persisted — `onSubmit` is the
 * only seam. M1-19/M1-20 are what give a submission somewhere real to go.
 */
export function FormRenderer({
  envelope,
  initialValues,
  onSubmit,
  onSaveDraft,
  uploadPhoto,
}: {
  envelope: FormEnvelope;
  initialValues?: Record<string, unknown>;
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  /**
   * Save what has been typed WITHOUT validating it (M1-20). Supplied by a
   * caller that has somewhere to keep a draft; omitted, no draft button renders.
   *
   * Deliberately skips `validate()`, unlike `onSubmit`: a draft is unfinished by
   * definition, and a half-filled checklist that refuses to save is worse than
   * no draft state at all. The server agrees — a Draft submission is not checked
   * against the schema either (api/CONVENTIONS.md, M1-19).
   */
  onSaveDraft?: (values: Record<string, unknown>) => void | Promise<void>;
  /** See `FormRendererContext` — omit to fall back to a local-only photo preview. */
  uploadPhoto?: (file: File) => Promise<string>;
}) {
  const { t } = useTranslation();
  const form = useFormRenderer(envelope, initialValues);
  const [submitting, setSubmitting] = useState(false);

  const renderState = {
    values: form.values,
    errors: form.errors,
    rules: envelope.uiSchema.rules,
    setValue: form.setValue,
    addRow: form.addRow,
    removeRow: form.removeRow,
  };

  async function handleSubmit() {
    if (!form.validate()) {
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(form.values);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSaveDraft() {
    if (!onSaveDraft) {
      return;
    }
    setSubmitting(true);
    try {
      await onSaveDraft(form.values);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <FormRendererProvider value={{ uploadPhoto }}>
      <div className="space-y-4">
        {form.fields.map((field) => (
          <FieldSwitch key={field.path} field={field} state={renderState} />
        ))}

        {form.errors.length > 0 ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {t('forms.hasErrors', { count: form.errors.length })}
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {onSaveDraft ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={submitting}
              onClick={() => void handleSaveDraft()}
            >
              {t('forms.saveDraft')}
            </Button>
          ) : null}
          <Button type="button" size="sm" disabled={submitting} onClick={() => void handleSubmit()}>
            {submitting ? t('forms.submitting') : t('forms.submit')}
          </Button>
        </div>
      </div>
    </FormRendererProvider>
  );
}
